<template>
  <div class="gx-member">
    <stars-canvas
      :count="90"
      :meteor-max="2"
      :meteor-gap="900"
      :star-colors="memberStarColors"
      fixed
    />
    <float-pts :count="12" :colors="memberPointColors" fixed />
    <glow-orb
      color="rgba(212,169,70,.35)"
      :size="560"
      x="15%"
      y="18%"
      :op="0.07"
      fixed
    />
    <glow-orb
      color="rgba(212,169,70,.22)"
      :size="420"
      x="84%"
      y="58%"
      :op="0.06"
      fixed
    />
    <new-ui-nav
      active="member"
      :is-logged-in="isLoggedIn"
      register-label="注册"
      compact-auth
      @home="goHome"
      @login="goLogin"
      @register="goRegister"
      @about="goAbout"
      @member="goMember"
      @enter="goEnter"
    />

    <main class="gx-member__content">
      <section
        class="gx-member__hero"
        :class="{ 'gx-member__hero--show': mounted }"
      >
        <div class="gx-member__halo"></div>
        <div class="gx-member__eyebrow"><span></span> MEMBERSHIP PLANS</div>
        <h1>会员权益</h1>
        <p>选择适合您的计划，开启AI智能运营新时代</p>
      </section>

      <section v-if="loading" class="gx-member__state">
        正在加载会员权益配置…
      </section>
      <section
        v-else-if="loadError"
        class="gx-member__state gx-member__state--error"
      >
        <p>{{ loadError }}</p>
        <button @click="loadPlans">重新加载</button>
      </section>

      <template v-else>
        <section class="gx-member__plans">
          <article
            v-for="(plan, index) in plans"
            :key="plan.level_key"
            class="gx-member__plan"
            :class="{
              'gx-member__plan--enterprise': isEnterprisePlan(plan),
              'gx-member__plan--hover': hoveredPlan === index,
            }"
            :style="planStyle(plan)"
            @mouseenter="hoveredPlan = index"
            @mouseleave="hoveredPlan = -1"
          >
            <span v-if="isEnterprisePlan(plan)" class="gx-member__plan-badge"
              >推荐</span
            >
            <div class="gx-member__plan-orb" :style="planOrbStyle(plan)">
              <span class="gx-member__plan-ring"></span
              ><span
                class="gx-member__plan-icon"
                v-html="planIcons[index]"
              ></span>
            </div>
            <h2>{{ plan.name }}</h2>
            <div class="gx-member__plan-price" :style="priceStyle(plan)">
              <span>{{ priceFor(plan) }}</span
              ><small v-if="isEnterprisePlan(plan) && lowestFee">{{
                lowestFee.card_key === "month" ? "/月起" : "起"
              }}</small>
            </div>
            <div
              v-if="isEnterprisePlan(plan) && lowestFee"
              class="gx-member__plan-per"
            >
              {{ feeLabels }}
            </div>
            <div
              class="gx-member__plan-divider"
              :style="dividerStyle(plan)"
            ></div>
            <div class="gx-member__plan-rows">
              <div
                v-for="benefit in previewBenefits(plan)"
                :key="benefit.key"
                class="gx-member__plan-row"
              >
                <span>{{ benefit.name }}</span
                ><strong :style="{ color: planColor(plan) }">{{
                  formatBenefit(benefit)
                }}</strong>
              </div>
            </div>
            <div v-if="savingSummary" class="gx-member__plan-save">
              <span>{{ savingTitle(plan) }}</span>
              <strong :style="{ color: planColor(plan) }">{{
                savingValue(plan)
              }}</strong>
              <small>{{
                isEnterprisePlan(plan)
                  ? "点击立即升级查看完整节省明细 →"
                  : "升级企业版可节省约 50%"
              }}</small>
            </div>
            <button
              class="gx-member__plan-btn"
              :class="{
                'gx-member__plan-btn--enterprise': isEnterprisePlan(plan),
              }"
              :disabled="balancePurchaseEnabled && isEnterprisePlan(plan) && !lowestFee"
              @click="isEnterprisePlan(plan) ? openUpgrade() : startFree()"
            >
              {{ actionText(plan) }}
            </button>
          </article>
        </section>

        <section class="gx-member__detail">
          <section-head
            en="COMPUTE POWER & SERVICES"
            zh="算力明细 & 服务权益"
            ac="var(--gx-member-accent)"
            :visible="true"
          />
          <div class="gx-member__table">
            <div class="gx-member__table-head">
              <div>功能 / 操作项</div>
              <div>{{ normalPlan ? normalPlan.name : "普通会员" }}</div>
              <div class="gx-member__table-enterprise">
                <span>推荐</span
                >{{ enterprisePlan ? enterprisePlan.name : "企业版" }}
              </div>
            </div>
            <template v-for="group in benefitGroups">
              <div
                v-for="row in group.rows"
                :key="row.key"
                class="gx-member__table-row"
              >
                <div class="gx-member__table-feature">
                  <i class="gx-member__feature-accent" aria-hidden="true"></i>
                  {{ row.name }}
                  <span v-if="row.name_badge" class="gx-member__item-badge">{{
                    row.name_badge
                  }}</span
                  >
                </div>
                <div
                  :class="{
                    'gx-member__benefit-free': isFreeBenefit(row.normal),
                  }"
                >
                  {{ formatBenefit(row.normal)
                  }}<small v-if="benefitBadge(row.normal)">{{
                    benefitBadge(row.normal)
                  }}</small>
                </div>
                <div
                  class="gx-member__enterprise-value"
                  :class="{
                    'gx-member__benefit-free': isFreeBenefit(row.enterprise),
                  }"
                >
                  {{ formatBenefit(row.enterprise)
                  }}<small v-if="benefitBadge(row.enterprise, row.normal)">{{
                    benefitBadge(row.enterprise, row.normal)
                  }}</small>
                </div>
              </div>
            </template>
          </div>
        </section>

        <section class="gx-member__cta">
          <h2>加入星人大家庭</h2>
          <p>与10,000+星人一起，让AI赋能您的电商之路</p>
          <div>
            <button
              class="gx-member__cta-primary"
              @click="isLoggedIn ? goEnter() : goRegister()"
            >
              {{ isLoggedIn ? "进入工作台" : "免费开始" }}</button
            ><button
              class="gx-member__cta-ghost"
              @click="isLoggedIn ? openUpgrade() : goLogin()"
            >
              {{
                isLoggedIn
                  ? isEnterprise
                    ? "续费企业版"
                    : "立即升级"
                  : "登录账户"
              }}
            </button>
          </div>
        </section>
      </template>
    </main>
    <footer class="gx-member__bottom">
      星观（杭州）数据科技有限公司 &nbsp;©&nbsp; 2026 &nbsp;·&nbsp;
      浙ICP备2021028918号
    </footer>

    <div v-if="showUpgrade" class="gx-member__modal" @click.self="closeUpgrade">
      <div class="gx-member__modal-box">
        <button class="gx-member__modal-close" @click="closeUpgrade">×</button>
        <template v-if="!balancePurchaseEnabled || !purchaseConfirm">
          <div class="gx-member__modal-plan-head">
            <span class="gx-member__modal-icon" v-html="planIcons[1]"></span>
            <div>
              <h2>企业版</h2>
              <p>1V1专属客服 · 无限店铺 · 算力全面优惠</p>
            </div>
          </div>
          <p class="gx-member__modal-label">选择套餐周期</p>
          <div v-if="fees.length" class="gx-member__modal-prices">
            <button
              v-for="fee in fees"
              :key="fee.id"
              :class="{
                'gx-member__modal-price--on': selectedFeeId === fee.id,
              }"
              @click="selectedFeeId = fee.id"
            >
              <i></i><span>{{ fee.name }}</span
              ><strong>¥{{ priceNumber(fee.selling_price) }}</strong
              ><small>{{ feePeriod(fee) }}</small
              ><em v-if="feeSavingLabel(fee)">{{ feeSavingLabel(fee) }}</em>
            </button>
          </div>
          <p v-else class="gx-member__modal-empty">
            当前没有可购买的企业版套餐。
          </p>
          <section class="gx-member__modal-discounts">
            <p>算力优惠明细</p>
            <div>
              <span
                v-for="benefit in enterpriseDiscountBenefits"
                :key="benefit.key"
              >
                {{ benefit.name }}<b>{{ benefit.badge }}</b>
              </span>
            </div>
          </section>
          <div v-if="savingSummary" class="gx-member__modal-summary">
            <span>1万件全流程合计节省</span>
            <strong>约 50%</strong>
<!--            <strong>约 {{ savingSummary.percent }}%</strong>-->
          </div>
          <p class="gx-member__modal-perks">
            <span v-for="perk in enterprisePerks" :key="perk"
              ><i>◉</i>{{ perk }}</span
            >
          </p>
          <button
            v-if="balancePurchaseEnabled"
            class="gx-member__modal-upgrade"
            :disabled="!selectedFee"
            @click="showPurchaseConfirm"
          >
            {{
              selectedFee
                ? `立即升级 ¥${priceNumber(
                    selectedFee.selling_price
                  )}${feePeriod(selectedFee)}`
                : "选择套餐"
            }}
          </button>
          <button
            v-else
            class="gx-member__modal-upgrade"
            @click="contactServiceForUpgrade"
          >
            联系客服升级
          </button>
          <p class="gx-member__modal-note">
            含税 · 支持对公转账 · 随时联系客服
          </p>
        </template>
        <template v-else-if="balancePurchaseEnabled">
          <h2>确认购买</h2>
          <p class="gx-member__modal-confirm">
            确认使用账户余额购买 {{ selectedFee.name }}，扣除
            <strong>¥{{ priceNumber(selectedFee.selling_price) }}</strong> 吗？
          </p>
          <div class="gx-member__modal-foot">
            <button
              class="gx-member__modal-ghost"
              :disabled="orderLoading"
              @click="purchaseConfirm = false"
            >
              返回修改</button
            ><button
              class="gx-member__modal-primary"
              :disabled="orderLoading"
              @click="confirmPurchase"
            >
              {{ orderLoading ? "购买中…" : "确认扣款购买" }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import StarsCanvas from "../../components/newUi/StarsCanvas.vue";
import FloatPts from "../../components/newUi/FloatPts.vue";
import GlowOrb from "../../components/newUi/GlowOrb.vue";
import SectionHead from "../../components/newUi/SectionHead.vue";
import NewUiNav from "../../components/newUi/NewUiNav.vue";

// 企业版升级、续费暂由客服办理，保留原余额购买流程。
const BALANCE_PURCHASE_ENABLED = false;

const PLAN_ICONS = [
  '<svg width="30" height="30" viewBox="0 0 30 30" fill="none"><path d="M15 26V16" stroke="#9CA3B5" stroke-width="2" stroke-linecap="round"/><path d="M15 16C13 12 8 10 4 12c2 5 8 7 11 4z" fill="#9CA3B5" opacity=".7"/><path d="M15 20C17 15 23 13 26 15c-2 5-8 7-11 5z" fill="#9CA3B5" opacity=".5"/></svg>',
  '<svg width="30" height="30" viewBox="0 0 30 30" fill="none"><rect x="13" y="22" width="4" height="7" rx="2" fill="#D4A946"/><polygon points="15,16 6,25 24,25" fill="#D4A946" opacity=".65"/><polygon points="15,10 7,18 23,18" fill="#D4A946" opacity=".82"/><polygon points="15,3 8,12 22,12" fill="#D4A946"/></svg>',
];

export default {
  name: "NewMemberPage",
  components: { StarsCanvas, FloatPts, GlowOrb, SectionHead, NewUiNav },
  data() {
    return {
      mounted: false,
      loading: true,
      loadError: "",
      hoveredPlan: -1,
      plans: [],
      fees: [],
      currentLevel: 9,
      showUpgrade: false,
      purchaseConfirm: false,
      selectedFeeId: null,
      orderLoading: false,
      planIcons: PLAN_ICONS,
      enterprisePerks: [
        "1V1 专属客服",
        // "无限店铺品牌",
        "SKU检测全免费",
        "店铺玩法共享",
      ],
      memberStarColors: ["#ffffff", "#ffffff", "#f0f4ff", "#d4a946", "#e8c778"],
      memberPointColors: ["#d4a946", "#f0c44a", "#e8c778"],
    };
  },
  computed: {
    balancePurchaseEnabled() {
      return BALANCE_PURCHASE_ENABLED;
    },
    isLoggedIn() {
      return !!this.$store.state.login.token;
    },
    normalPlan() {
      return this.plans.find((plan) => Number(plan.level_key) === 9) || null;
    },
    enterprisePlan() {
      return this.plans.find((plan) => Number(plan.level_key) === 1) || null;
    },
    isEnterprise() {
      return Number(this.currentLevel) === 1;
    },
    lowestFee() {
      return this.fees.length
        ? this.fees.reduce((lowest, fee) =>
            Number(fee.selling_price) < Number(lowest.selling_price)
              ? fee
              : lowest
          )
        : null;
    },
    selectedFee() {
      return (
        this.fees.find(
          (fee) => Number(fee.id) === Number(this.selectedFeeId)
        ) || null
      );
    },
    feeLabels() {
      return this.fees.map((fee) => fee.name).join(" · ");
    },
    benefitGroups() {
      if (!this.normalPlan || !this.enterprisePlan) return [];
      const enterpriseByKey = (this.enterprisePlan.benefits || []).reduce(
        (map, benefit) => {
          map[benefit.key] = benefit;
          return map;
        },
        {}
      );
      const groups = [
        { type: 1, title: "算力明细", rows: [] },
        { type: 2, title: "服务权益", rows: [] },
      ];
      (this.normalPlan.benefits || []).forEach((normal) => {
        const enterprise = enterpriseByKey[normal.key];
        const group = groups.find((item) => item.type === Number(normal.type));
        if (enterprise && group)
          group.rows.push({ ...normal, normal, enterprise });
      });
      return groups.filter((group) => group.rows.length);
    },
    enterpriseDiscountBenefits() {
      if (!this.normalPlan || !this.enterprisePlan) return [];
      const normalByKey = (this.normalPlan.benefits || []).reduce(
        (map, benefit) => {
          map[benefit.key] = benefit;
          return map;
        },
        {}
      );
      const displayOrder = [
        "ai_image_detection",
        "ai_source_search",
        "sku_detection",
        "ai_generate_title",
        "ai_generate_white_bg",
        "ai_replace_1688_sku",
      ];
      return displayOrder
        .map((key) => {
          const benefit = (this.enterprisePlan.benefits || []).find(
            (item) => item.key === key
          );
          const badge = this.benefitBadge(benefit, normalByKey[key]);
          return benefit && badge ? { ...benefit, badge } : null;
        })
        .filter(Boolean);
    },
    savingSummary() {
      if (!this.normalPlan || !this.enterprisePlan) return null;
      const total = (plan) => {
        const benefits = (plan.benefits || []).reduce((map, benefit) => {
          map[benefit.key] = benefit;
          return map;
        }, {});
        const amounts = [
          "distribution_power",
          "sku_detection",
          "ai_generate_title",
        ].map((key) => benefits[key] && Number(benefits[key].amount));
        if (amounts.some((amount) => !Number.isFinite(amount) || amount < 0))
          return null;
        return amounts.reduce((sum, amount) => sum + amount, 0) * 10000;
      };
      const normal = total(this.normalPlan);
      const enterprise = total(this.enterprisePlan);
      return normal === null || enterprise === null || normal <= 0
        ? null
        : {
            normal,
            enterprise,
            saved: this.formatNumber(Math.max(0, normal - enterprise)),
            percent: Math.max(0, Math.round((1 - enterprise / normal) * 100)),
          };
    },
  },
  mounted() {
    window.setTimeout(() => {
      this.mounted = true;
    }, 80);
    this.loadPlans();
  },
  methods: {
    ...mapActions({
      fetchMembershipPlans: "ucenter/fetchMembershipPlans",
      fetchCurrentMembership: "ucenter/fetchCurrentMembership",
      purchaseMembership: "ucenter/purchaseMembership",
    }),
    loadPlans() {
      this.loading = true;
      this.loadError = "";
      this.fetchMembershipPlans({
        data: {},
        success: (res) => {
          this.loading = false;
          if (res.code !== 200 || !res.data) {
            this.loadError = res.message || "会员权益配置加载失败";
            return;
          }
          this.plans = Array.isArray(res.data.plans) ? res.data.plans : [];
          this.fees = Array.isArray(res.data.fees) ? res.data.fees : [];
          this.selectedFeeId = this.lowestFee ? this.lowestFee.id : null;
          if (this.isLoggedIn) this.loadCurrentMembership();
        },
        error: () => {
          this.loading = false;
          this.loadError = "会员权益配置加载失败，请稍后重试";
        },
      });
    },
    loadCurrentMembership() {
      this.fetchCurrentMembership({
        data: {},
        success: (res) => {
          if (res.code === 200 && res.data) {
            this.currentLevel = Number(res.data.level) || 9;
            this.$store.commit("login/SET_USER_LEVEL", this.currentLevel);
          }
        },
      });
    },
    isEnterprisePlan(plan) {
      return Number(plan.level_key) === 1;
    },
    planColor(plan) {
      return this.isEnterprisePlan(plan) ? "#D4A946" : "#9CA3B5";
    },
    planStyle(plan) {
      return {
        borderColor: this.isEnterprisePlan(plan)
          ? "rgba(212,169,70,.65)"
          : "rgba(107,114,128,.28)",
        boxShadow: this.isEnterprisePlan(plan)
          ? "0 0 60px rgba(212,169,70,.12), 0 20px 64px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.07)"
          : "0 4px 28px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.04)",
        "--plan-color": this.planColor(plan),
      };
    },
    planOrbStyle(plan) {
      return {
        borderColor: this.planColor(plan) + "55",
        background: this.planColor(plan) + "13",
      };
    },
    priceStyle() {
      return {};
    },
    dividerStyle(plan) {
      return {
        background: `linear-gradient(90deg,transparent,${this.planColor(
          plan
        )}66,transparent)`,
      };
    },
    priceFor(plan) {
      return this.isEnterprisePlan(plan)
        ? this.lowestFee
          ? `¥${this.priceNumber(this.lowestFee.selling_price)}`
          : "暂未开放"
        : "免费";
    },
    actionText(plan) {
      if (!this.isEnterprisePlan(plan))
        return this.isLoggedIn ? "进入工作台" : "免费开始";
      if (BALANCE_PURCHASE_ENABLED && !this.lowestFee) return "暂未开放";
      return this.isEnterprise ? "立即续费 →" : "立即升级 →";
    },
    previewBenefits(plan) {
      return (plan.benefits || [])
        .filter((benefit) => Number(benefit.type) === 2)
        .slice(0, 2);
    },
    formatBenefit(benefit) {
      if (!benefit) return "—";
      if (Number(benefit.type) === 1) {
        const amount = Number(benefit.amount);
        if (Number.isFinite(amount))
          return amount === 0
            ? "免费"
            : `${this.formatNumber(amount)}${
                benefit.unit ? ` ${benefit.unit}` : ""
              }`;
      }
      return benefit.amount === null || benefit.amount === ""
        ? "—"
        : String(benefit.amount);
    },
    isFreeBenefit(benefit) {
      return this.formatBenefit(benefit) === "免费";
    },
    formatNumber(value) {
      const number = Number(value);
      return Number.isInteger(number)
        ? String(number)
        : String(number)
            .replace(/(\.\d*?)0+$/, "$1")
            .replace(/\.$/, "");
    },
    priceNumber(value) {
      const number = Number(value);
      return Number.isFinite(number)
        ? new Intl.NumberFormat("en-US", {
            maximumFractionDigits: 2,
          }).format(number)
        : this.formatNumber(value);
    },
    feePeriod(fee) {
      const periods = {
        month: "/月",
        half_year: "/半年",
        year: "/年",
      };
      return periods[fee.card_key] || `/${fee.duration_days}天`;
    },
    feeSavingLabel(fee) {
      const original = Number(fee.original_price);
      const selling = Number(fee.selling_price);
      if (
        !Number.isFinite(original) ||
        !Number.isFinite(selling) ||
        original <= selling
      )
        return "";
      return `省${Math.round((1 - selling / original) * 100)}%`;
    },
    benefitBadge(benefit, normalBenefit = null) {
      if (!benefit) return "";
      if (benefit.badge) return benefit.badge;
      if (!normalBenefit || Number(benefit.type) !== 1) return "";
      const normalAmount = Number(normalBenefit.amount);
      const amount = Number(benefit.amount);
      if (
        !Number.isFinite(normalAmount) ||
        !Number.isFinite(amount) ||
        normalAmount <= 0 ||
        amount >= normalAmount
      )
        return "";
      return `省${Math.round((1 - amount / normalAmount) * 100)}%`;
    },
    savingTitle(plan) {
      return this.isEnterprisePlan(plan)
        ? "1万件全流程节省"
        : "1万件全流程参考消耗";
    },
    savingValue(plan) {
      return this.isEnterprisePlan(plan)
        ? `约 50%`
        // ? `约 ${this.savingSummary.percent}%`
        : "基础消耗";
    },
    startFree() {
      this.isLoggedIn ? this.goEnter() : this.goRegister();
    },
    openUpgrade() {
      if (!this.isLoggedIn) {
        this.goLogin();
        return;
      }
      if (BALANCE_PURCHASE_ENABLED && !this.lowestFee) {
        this.$message.error("当前没有可购买的企业版套餐");
        return;
      }
      this.selectedFeeId = this.selectedFeeId || (this.lowestFee ? this.lowestFee.id : null);
      this.showUpgrade = true;
      this.purchaseConfirm = false;
    },
    closeUpgrade() {
      if (!BALANCE_PURCHASE_ENABLED || !this.orderLoading) {
        this.showUpgrade = false;
        this.purchaseConfirm = false;
      }
    },
    showPurchaseConfirm() {
      if (!BALANCE_PURCHASE_ENABLED) return;
      if (this.selectedFee) this.purchaseConfirm = true;
    },
    contactServiceForUpgrade() {
      this.closeUpgrade();
      this.$customerService.open();
    },
    confirmPurchase() {
      if (!BALANCE_PURCHASE_ENABLED) return;
      if (!this.selectedFee || this.orderLoading) return;
      this.orderLoading = true;
      this.purchaseMembership({
        data: { fee_id: this.selectedFee.id },
        success: (res) => {
          this.orderLoading = false;
          if (res.code !== 200) {
            this.$message.error(res.message || "购买失败");
            return;
          }
          this.$message.success(res.message || "企业版购买成功");
          this.closeUpgrade();
          this.loadCurrentMembership();
        },
        error: () => {
          this.orderLoading = false;
          this.$message.error("购买请求失败，请稍后重试");
        },
      });
    },
    goHome() {
      this.$router.push("/");
    },
    goLogin() {
      this.$router.push("/login");
    },
    goRegister() {
      this.$router.push("/register");
    },
    goMember() {
      this.$router.push("/vip");
    },
    goAbout() {
      this.$router.push("/about");
    },
    goEnter() {
      this.$router.push(this.isLoggedIn ? "/ucenter?menuId=113" : "/login");
    },
  },
};
</script>

<style scoped>
.gx-member {
  --gx-member-scrollbar-width: 0px;
  --gx-member-bg-0: #070e22;
  --gx-member-bg-1: #0d1630;
  --gx-member-bg-2: #152248;
  --gx-member-surface: rgba(255, 255, 255, 0.035);
  --gx-member-text: #f0ebe0;
  --gx-member-muted: #8a95b0;
  --gx-member-accent: #d4a946;
  --gx-member-accent-light: #f0c44a;
  --gx-member-accent-strong: #e4b740;
  --gx-member-accent-muted: rgba(212, 169, 70, .55);
  --gx-member-accent-soft: rgba(212, 169, 70, .12);
  --gx-member-accent-line: rgba(212, 169, 70, .16);
  --gx-member-accent-border: rgba(212, 169, 70, .45);
  --gx-member-success: #4caf7a;
  --gx-member-nav-bg: rgba(7, 14, 34, .94);
  --gx-member-nav-text: rgba(240, 235, 224, .55);
  --gx-member-on-accent: #071025;
  --gx-member-font-sans: "Noto Sans SC", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  --gx-member-font-display: "Noto Serif SC", Georgia, serif;
  --gx-member-font-mono: "JetBrains Mono", "SFMono-Regular", Consolas, monospace;
  --gx-member-ease: cubic-bezier(0.22, 1, 0.36, 1);
  --gx-member-motion-fast: 0.25s;
  --gx-member-motion-base: 0.5s;
  --gx-member-motion-slow: 0.85s;
  --gx-member-orb-duration: 14s;
  --gx-member-float-duration: 8s;
  min-height: 100vh;
  overflow-x: hidden;
  background: linear-gradient(
    180deg,
    var(--gx-member-bg-0) 0%,
    var(--gx-member-bg-1) 22%,
    var(--gx-member-bg-2) 55%,
    var(--gx-member-bg-1) 82%,
    var(--gx-member-bg-0) 100%
  );
  color: var(--gx-member-text);
  font-family: var(--gx-member-font-sans);
}
.gx-member,
.gx-member * {
  font-family: var(--gx-member-font-sans);
}
.gx-member__content {
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  padding: 64px 28px 96px;
  transform: translateX(calc(var(--gx-member-scrollbar-width) / -2));
}
.gx-member__hero {
  position: relative;
  text-align: center;
  width: calc(100vw - 48px - var(--gx-member-scrollbar-width));
  margin-left: 50%;
  padding: 86.5px 0 65.5px;
  opacity: 0;
  transform: translate(-50%, 22px);
  transition: all var(--gx-member-motion-slow) var(--gx-member-ease);
}
.gx-member__hero--show {
  opacity: 1;
  transform: translate(-50%, 0);
}
.gx-member__halo {
  position: absolute;
  width: 280px;
  height: 145px;
  left: 50%;
  top: 28px;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(
    ellipse,
    rgba(212, 169, 70, 0.16),
    transparent 68%
  );
  filter: blur(9px);
}
.gx-member__eyebrow {
  position: relative;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  border: 1px solid rgba(212, 169, 70, 0.32);
  border-radius: 99px;
  padding: 7px 18px;
  color: var(--gx-member-accent);
  font: 700 10px/1 var(--gx-member-font-mono);
  letter-spacing: 4px;
}
.gx-member__eyebrow span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--gx-member-accent);
  box-shadow: 0 0 9px var(--gx-member-accent);
}
.gx-member__hero h1 {
  position: relative;
  margin: 24px 0 16px;
  background: linear-gradient(
    135deg,
    var(--gx-member-text) 0%,
    var(--gx-member-accent) 40%,
    var(--gx-member-accent-light) 60%,
    var(--gx-member-text) 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gx-shimmer 7s 1s linear infinite;
  font: 700 58px/1.15 var(--gx-member-font-display);
  letter-spacing: 0.03em;
}
.gx-member__hero p {
  position: relative;
  margin: 0;
  color: var(--gx-member-muted);
  font-size: 16px;
}
.gx-member__state {
  max-width: 680px;
  margin: 20px auto 100px;
  padding: 34px;
  border: 1px solid rgba(212, 169, 70, 0.22);
  border-radius: 18px;
  text-align: center;
  color: #9ca3b5;
  background: rgba(255, 255, 255, 0.035);
}
.gx-member__state--error {
  color: #f0c44a;
}
.gx-member__state p {
  margin: 0 0 15px;
}
.gx-member__state button {
  border: 1px solid rgba(212, 169, 70, 0.5);
  border-radius: 9px;
  padding: 8px 18px;
  background: transparent;
  color: #d4a946;
  cursor: pointer;
}
.gx-member__plans {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 388px));
  justify-content: center;
  align-items: stretch;
  gap: 28px;
  margin-bottom: 91.5px;
}
.gx-member__plan {
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 542px;
  overflow: visible;
  padding: 36px 24px 28px;
  border: 1px solid;
  border-radius: 24px;
  background: var(--gx-member-surface);
  transition: transform var(--gx-member-motion-fast) var(--gx-member-ease),
    box-shadow var(--gx-member-motion-fast) var(--gx-member-ease);
}
.gx-member__plan--enterprise {
  transform: translateY(-12px);
}
.gx-member__plan--hover {
  transform: translateY(-8px);
}
.gx-member__plan--enterprise.gx-member__plan--hover {
  transform: translateY(-19px);
}
.gx-member__plan-badge {
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 5px 22px;
  border-radius: 99px;
  background: var(--gx-member-accent-strong);
  color: var(--gx-member-bg-0);
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 0 24px rgba(228, 183, 64, 0.62);
  white-space: nowrap;
  z-index: 1;
}
.gx-member__plan-orb {
  position: relative;
  flex: 0 0 auto;
  width: 70px;
  height: 70px;
  margin: 0 auto 18px;
  border: 1px solid;
  border-radius: 50%;
  display: grid;
  place-items: center;
}
.gx-member__plan-ring {
  position: absolute;
  inset: -5px;
  border: 1px dashed var(--plan-color);
  border-radius: 50%;
  opacity: 0.35;
  animation: gx-member-spin 12s linear infinite;
}
.gx-member__plan-icon {
  display: flex;
}
.gx-member__plan h2 {
  margin: 0 0 16px;
  text-align: center;
  color: rgba(220, 215, 240, 0.9);
  font: 700 20px/30px var(--gx-member-font-display);
  letter-spacing: 0.04em;
}
.gx-member__plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  text-align: center;
  min-height: 60px;
  font: 800 40px/60px var(--gx-member-font-mono);
  letter-spacing: 0;
}
.gx-member__plan-price span {
  background: linear-gradient(135deg, #f0ebe0 0%, #9ca3b5 50%, #f0ebe0 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.gx-member__plan-price small {
  margin-left: 4px;
  color: #8a95b0;
  font: 13px/19.5px var(--gx-member-font-sans);
  letter-spacing: 0;
}
.gx-member__plan--enterprise .gx-member__plan-price {
  min-height: 54px;
  font: 800 36px/54px var(--gx-member-font-mono);
}
.gx-member__plan--enterprise .gx-member__plan-price span {
  background: linear-gradient(135deg, #d4a946, #f0c44a);
  -webkit-background-clip: text;
  background-clip: text;
}
.gx-member__plan-per {
  min-height: 19.5px;
  margin-top: 0;
  text-align: center;
  color: #8a95b0;
  font: 10px/19.5px "Noto Sans SC", sans-serif;
}
.gx-member__plan--enterprise .gx-member__plan-per {
  min-height: 13.5px;
  margin-top: -1.75px;
  font-size: 10px;
  line-height: 13.5px;
}
.gx-member__plan-divider {
  height: 1px;
  margin: 20px 0 16px;
}
.gx-member__plan--enterprise .gx-member__plan-divider {
  margin-top: 14.25px;
}
.gx-member__plan-rows {
  flex: 0 0 auto;
}
.gx-member__plan-row {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 10.75px 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.055);
  color: rgba(175, 168, 200, 0.7);
  font: 13px/19.5px "Noto Sans SC", sans-serif;
}
.gx-member__plan-row strong {
  max-width: 58%;
  text-align: right;
  font: 700 12.5px/17.5px "Noto Sans SC", sans-serif;
}
.gx-member__plan-save {
  display: grid;
  grid-template-columns: 1fr auto;
  margin: 20px 0;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
}
.gx-member__plan-save span {
  color: #8a95b0;
  font: 12px/18px "Noto Sans SC", sans-serif;
}
.gx-member__plan-save strong {
  font: 700 16px/22.5px "JetBrains Mono", Inter, monospace;
}
.gx-member__plan-save small {
  grid-column: 1/-1;
  margin-top: 4px;
  color: #6f7690;
  font: 11px/16.5px "Noto Sans SC", sans-serif;
}
.gx-member__plan-btn {
  width: 100%;
  min-height: 50px;
  border: 1px solid rgba(156, 163, 181, 0.314);
  border-radius: 13px;
  background: transparent;
  color: #9ca3b5;
  cursor: pointer;
  font: 700 15px var(--gx-member-font-display);
  transition: transform 0.25s, box-shadow 0.25s;
}
.gx-member__plan-btn--enterprise {
  border: 0;
  background: linear-gradient(135deg, #d4a946, #f0c44a);
  color: #071025;
  box-shadow: 0 4px 22px rgba(212, 169, 70, 0.5);
}
.gx-member__plan-btn:not(:disabled):hover {
  transform: scale(1.025);
}
.gx-member__plan-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.gx-member__save-banner {
  margin: 0 auto 96px;
  max-width: 880px;
  padding: 30px 36px;
  border: 1px solid rgba(212, 169, 70, 0.22);
  border-radius: 22px;
  background: linear-gradient(
    135deg,
    rgba(212, 169, 70, 0.09),
    rgba(255, 255, 255, 0.035)
  );
  box-shadow: 0 10px 46px rgba(0, 0, 0, 0.4);
}
.gx-member__save-en {
  color: #d4a946;
  font: 700 10px Inter, monospace;
  letter-spacing: 3px;
}
.gx-member__save-banner h2 {
  margin: 10px 0 4px;
  font: 700 20px "Noto Serif SC", serif;
}
.gx-member__save-banner em {
  color: #d4a946;
  font-style: normal;
}
.gx-member__save-banner p {
  margin: 0 0 20px;
  color: #8a95b0;
  font-size: 12px;
}
.gx-member__save-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.gx-member__save-cell {
  display: grid;
  gap: 7px;
  justify-items: center;
  padding: 18px;
  border: 1px solid;
  border-radius: 14px;
  background: rgba(7, 14, 34, 0.3);
}
.gx-member__save-cell span {
  color: #aab2c6;
  font-size: 13px;
}
.gx-member__save-cell strong {
  font: 700 22px "JetBrains Mono", Inter, monospace;
}
.gx-member__save-cell small {
  color: #8a95b0;
  font-size: 12px;
}
.gx-member__detail {
  box-sizing: border-box;
  width: 880px;
  max-width: calc(100% + 20px);
  margin: 0 -10px;
  padding: 0 24px 100px;
}
.gx-member__table {
  overflow: hidden;
  border: 1px solid rgba(212, 169, 70, 0.22);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.035);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
.gx-member__table-head,
.gx-member__table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
}
.gx-member__table-head {
  background: rgba(255, 255, 255, 0.035);
  border-bottom: 1px solid rgba(212, 169, 70, 0.24);
  color: #8a95b0;
  font: 700 13px/19.5px "Noto Sans SC", sans-serif;
  letter-spacing: 0.03em;
}
.gx-member__table-head > div,
.gx-member__table-row > div {
  padding: 14px 22px;
}
.gx-member__table-head > div:not(:first-child),
.gx-member__table-row > div:not(:first-child) {
  text-align: center;
}
.gx-member__table-enterprise {
  color: #d4a946;
}
.gx-member__table-enterprise span {
  margin-right: 5px;
  padding: 2px 6px;
  border-radius: 7px;
  color: #d4a946;
  background: rgba(212, 169, 70, 0.16);
  font-size: 10px;
}
.gx-member__table-group {
  padding: 10px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(212, 169, 70, 0.055);
  color: #d4a946;
  font: 700 11px Inter, monospace;
  letter-spacing: 2px;
}
.gx-member__table-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.055);
  color: #cdd3df;
  font: 600 13px/19.5px "Noto Sans SC", sans-serif;
}
.gx-member__table-feature {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f0ebe0;
}
.gx-member__feature-accent {
  width: 2px;
  height: 14px;
  flex: 0 0 auto;
  border-radius: 1px;
  background: rgba(212, 169, 70, 0.28);
}
.gx-member__item-badge {
  margin-right: 7px;
  padding: 2px 6px;
  border-radius: 6px;
  color: #d4a946;
  background: rgba(212, 169, 70, 0.13);
  font-size: 10px;
}
.gx-member__enterprise-value {
  color: #d4a946;
}
.gx-member__benefit-free {
  color: var(--gx-member-success);
}
.gx-member__enterprise-value small {
  display: inline;
  margin-left: 5px;
  padding: 1px 5px;
  border: 1px solid rgba(212, 169, 70, 0.28);
  border-radius: 4px;
  color: #d4a946;
  background: rgba(212, 169, 70, 0.12);
  font: 600 9px/13.5px "Noto Sans SC", Inter, monospace;
}
.gx-member__cta {
  max-width: 630px;
  margin: 0 auto;
  padding: 52px 35px;
  border: 1px solid rgba(212, 169, 70, 0.25);
  border-radius: 24px;
  text-align: center;
  background: linear-gradient(
    145deg,
    rgba(212, 169, 70, 0.09),
    rgba(255, 255, 255, 0.035)
  );
  box-shadow: 0 18px 58px rgba(0, 0, 0, 0.42);
}
.gx-member__cta h2 {
  margin: 0 0 12px;
  color: #f0ebe0;
  font: 700 28px var(--gx-member-font-display);
}
.gx-member__cta p {
  margin: 0 0 28px;
  color: #9ca3b5;
}
.gx-member__cta > div {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
}
.gx-member__cta button {
  min-height: 47px;
  padding: 0 30px;
  border-radius: 12px;
  cursor: pointer;
  font: 700 15px var(--gx-member-font-display);
}
.gx-member__cta-primary {
  border: 0;
  color: #071025;
  background: linear-gradient(135deg, #d4a946, #f0c44a);
}
.gx-member__cta-ghost {
  border: 1px solid rgba(212, 169, 70, 0.46);
  color: #d4a946;
  background: transparent;
}
.gx-member__bottom {
  position: relative;
  z-index: 1;
  padding: 19px 25px;
  border-top: 1px solid rgba(212, 169, 70, 0.1);
  background: rgba(4, 8, 22, 0.92);
  color: rgba(138, 149, 176, 0.5);
  text-align: center;
  font-size: 12px;
}
.gx-member__modal {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(4, 8, 22, 0.84);
  backdrop-filter: blur(12px);
}
.gx-member__modal-box {
  position: relative;
  box-sizing: border-box;
  width: min(500px, 100%);
  max-height: calc(100dvh - 40px);
  overflow-y: auto;
  padding: 26px 28px 20px;
  border: 1px solid rgba(212, 169, 70, 0.5);
  border-radius: 24px;
  background: linear-gradient(145deg, #142044, #0d1732 68%, #091127);
  box-shadow: 0 0 54px rgba(212, 169, 70, 0.15), 0 30px 90px rgba(0, 0, 0, 0.7);
}
.gx-member__modal-box h2 {
  margin: 0 0 8px;
  color: #f0ebe0;
  font: 700 24px/1.2 var(--gx-member-font-display);
}
.gx-member__modal-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 27px;
  height: 27px;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 50%;
  color: #aab2c6;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
  font-size: 18px;
}
.gx-member__modal-plan-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.gx-member__modal-icon {
  position: relative;
  display: grid;
  width: 46px;
  height: 46px;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgba(212, 169, 70, 0.45);
  border-radius: 50%;
  background: rgba(212, 169, 70, 0.08);
  box-shadow: 0 0 14px rgba(212, 169, 70, 0.16);
  animation: gx-iconFloat 3s var(--gx-member-ease) infinite;
}
.gx-member__modal-icon::before {
  position: absolute;
  inset: -5px;
  border: 1px dashed rgba(212, 169, 70, 0.48);
  border-radius: inherit;
  content: "";
  opacity: 0.75;
  animation: gx-member-spin 8s linear infinite;
}
.gx-member__modal-icon svg {
  position: relative;
  z-index: 1;
  animation: gx-seedGlow 3s ease-in-out infinite;
}
.gx-member__modal-plan-head p,
.gx-member__modal-empty,
.gx-member__modal-confirm {
  margin: 0;
  color: #9ca3b5;
  line-height: 1.4;
  font-size: 12px;
}
.gx-member__modal-label,
.gx-member__modal-discounts > p {
  margin: 0 0 9px;
  color: #d4a946;
  text-align: center;
  font: 700 10px/1 Inter, monospace;
  letter-spacing: 2px;
}
.gx-member__modal-confirm strong {
  color: #f0c44a;
}
.gx-member__modal-prices {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
  margin-bottom: 16px;
}
.gx-member__modal-prices button {
  position: relative;
  display: grid;
  justify-items: center;
  gap: 2px;
  min-height: 104px;
  padding: 10px 7px 7px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 10px;
  color: #cdd3df;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
}
.gx-member__modal-prices button i {
  width: 9px;
  height: 9px;
  border: 1px solid rgba(156, 163, 181, 0.65);
  border-radius: 50%;
}
.gx-member__modal-prices button span {
  color: #aab2c6;
  font-size: 13px;
}
.gx-member__modal-prices button strong {
  color: #d4a946;
  font: 700 18px/1 "JetBrains Mono", Inter, monospace;
}
.gx-member__modal-prices button small {
  color: #8a95b0;
  font-size: 11px;
}
.gx-member__modal-prices button em {
  color: #8a95b0;
  font: normal 10px/1 "Noto Sans SC", sans-serif;
}
.gx-member__modal-prices .gx-member__modal-price--on {
  border-color: #d4a946;
  background: rgba(212, 169, 70, 0.12);
}
.gx-member__modal-prices .gx-member__modal-price--on i {
  border: 3px solid #e4b740;
  box-sizing: border-box;
}
.gx-member__modal-prices .gx-member__modal-price--on span,
.gx-member__modal-prices .gx-member__modal-price--on strong {
  color: #f0c44a;
}
.gx-member__modal-discounts {
  margin-bottom: 12px;
}
.gx-member__modal-discounts > p {
  text-align: left;
  font-size: 10px;
  letter-spacing: 1.5px;
}
.gx-member__modal-discounts > div {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}
.gx-member__modal-discounts span {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 32px;
  padding: 0 9px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  color: #aab2c6;
  background: rgba(255, 255, 255, 0.035);
  font-size: 11px;
}
.gx-member__modal-discounts b {
  padding: 2px 5px;
  border-radius: 4px;
  color: #55c69a;
  background: rgba(53, 171, 125, 0.16);
  font-size: 10px;
}
.gx-member__modal-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 40px;
  margin-bottom: 11px;
  padding: 0 10px;
  border: 1px solid rgba(212, 169, 70, 0.26);
  border-radius: 8px;
  color: #f0ebe0;
  background: rgba(212, 169, 70, 0.04);
  font-size: 13px;
}
.gx-member__modal-summary strong {
  color: #f0c44a;
  font: 700 20px "JetBrains Mono", Inter, monospace;
}
.gx-member__modal-perks {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px 9px;
  margin: 0 0 13px;
  color: #9ca3b5;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.45;
}
.gx-member__modal-perks span {
  white-space: nowrap;
}
.gx-member__modal-perks i {
  margin-right: 3px;
  color: var(--gx-member-accent);
  font-style: normal;
}
.gx-member__modal-upgrade {
  width: 100%;
  min-height: 50px;
  border: 0;
  border-radius: 11px;
  color: #071025;
  background: linear-gradient(135deg, #d4a946, #f0c44a);
  box-shadow: 0 4px 20px rgba(212, 169, 70, 0.45);
  cursor: pointer;
  font: 700 15px var(--gx-member-font-display);
}
.gx-member__modal-upgrade:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.gx-member__modal-note {
  margin: 8px 0 0;
  color: rgba(138, 149, 176, 0.5);
  text-align: center;
  font-size: 10px;
}
.gx-member__modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.gx-member__modal-foot button {
  min-height: 43px;
  padding: 0 21px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
}
.gx-member__modal-ghost {
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #aab2c6;
  background: transparent;
}
.gx-member__modal-primary {
  border: 0;
  color: #071025;
  background: linear-gradient(135deg, #d4a946, #f0c44a);
}
.gx-member__modal-foot button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
@keyframes gx-member-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (min-width: 701px) {
  .gx-member {
    /* 参考页按浏览器可视内容区居中；商户端全局 body 使用 100vw，桌面端在本页抵消滚动条宽度。 */
    --gx-member-scrollbar-width: 15px;
  }
  .gx-member__plan {
    height: 542px;
    min-height: 0;
  }
}
@media (max-width: 700px) {
  .gx-member__content {
    padding: 88px 16px 64px;
  }
  .gx-member__hero h1 {
    font-size: 42px;
  }
  .gx-member__hero p {
    font-size: 14px;
  }
  .gx-member__plans {
    grid-template-columns: 1fr;
    gap: 22px;
  }
  .gx-member__plan--enterprise,
  .gx-member__plan--enterprise.gx-member__plan--hover {
    transform: none;
  }
  .gx-member__save-banner {
    padding: 24px 20px;
  }
  .gx-member__table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .gx-member__table-head,
  .gx-member__table-row {
    min-width: 560px;
  }
  .gx-member__modal-box {
    padding: 34px 20px 22px;
  }
  .gx-member__modal-prices {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .gx-member *,
  .gx-member *::before,
  .gx-member *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
